import {
  PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SidebarDropTarget } from './SidebarCustomization';

type Drag = {
  path: string;
  text: string;
  pointerId: number;
  source: HTMLElement;
  startX: number;
  startY: number;
  x: number;
  y: number;
  active: boolean;
};

type Options = {
  enabled: boolean;
  availablePaths: string[];
  onDrop: (path: string, target: SidebarDropTarget | undefined) => void;
  onClose: (restoreFocus: boolean) => void;
};

export const useSidebarDrag = (options: Options) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag>();
  const suppressClick = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [preview, setPreview] = useState<{
    path: string;
    text: string;
    x: number;
    y: number;
    target: SidebarDropTarget | undefined;
  }>();

  const getPosition = (drag: Drag) => {
    const sidebar = sidebarRef.current?.getBoundingClientRect();
    if (
      !sidebar ||
      drag.x < sidebar.left ||
      drag.x >= sidebar.right ||
      drag.y < 0 ||
      drag.y >= window.innerHeight
    ) {
      return undefined;
    }

    const sections = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(
        '[data-sidebar-section]',
      ) ?? [],
    );
    const section = sections.find((element, index) => {
      const next = sections[index + 1];
      return (
        !next ||
        drag.y <
          (element.getBoundingClientRect().bottom +
            next.getBoundingClientRect().top) /
            2
      );
    });
    if (!section) return undefined;
    const rows = section.querySelectorAll<HTMLElement>('[data-sidebar-path]');
    const before = Array.from(rows ?? []).find(row => {
      const rect = row.getBoundingClientRect();
      return (
        row.dataset.sidebarPath !== drag.path &&
        drag.y < rect.top + rect.height / 2
      );
    });
    return {
      section: Number(section.dataset.sidebarSection),
      before: before?.dataset.sidebarPath ?? null,
    };
  };

  useEffect(() => {
    if (!options.enabled) {
      return undefined;
    }

    let animationFrame: number | undefined;
    const cancel = () => {
      const drag = dragRef.current;
      dragRef.current = undefined;
      if (drag?.source.hasPointerCapture?.(drag.pointerId)) {
        drag.source.releasePointerCapture(drag.pointerId);
      }
      setPreview(undefined);
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
    };
    const updatePreview = (drag: Drag) => {
      setPreview({
        path: drag.path,
        text: drag.text,
        x: drag.x,
        y: drag.y,
        target: getPosition(drag),
      });
    };
    const scroll = () => {
      const drag = dragRef.current;
      const list = listRef.current;
      if (!drag?.active || !list) {
        return;
      }
      const rect = list.getBoundingClientRect();
      if (getPosition(drag) !== undefined) {
        let delta = 0;
        if (drag.y < rect.top + 32) {
          delta = -8;
        } else if (drag.y > rect.bottom - 32) {
          delta = 8;
        }
        if (delta) {
          const previousScroll = list.scrollTop;
          list.scrollTop += delta;
          if (list.scrollTop !== previousScroll) {
            updatePreview(drag);
          }
        }
      }
      animationFrame = requestAnimationFrame(scroll);
    };
    const move = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      if (!optionsRef.current.availablePaths.includes(drag.path)) {
        cancel();
        return;
      }
      drag.x = event.clientX;
      drag.y = event.clientY;
      if (
        !drag.active &&
        Math.hypot(drag.x - drag.startX, drag.y - drag.startY) < 5
      ) {
        return;
      }
      event.preventDefault();
      if (!drag.active) {
        drag.active = true;
        suppressClick.current = true;
        animationFrame = requestAnimationFrame(scroll);
      }
      updatePreview(drag);
    };
    const drop = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      if (drag.active) {
        event.preventDefault();
        drag.x = event.clientX;
        drag.y = event.clientY;
        if (optionsRef.current.availablePaths.includes(drag.path)) {
          optionsRef.current.onDrop(drag.path, getPosition(drag));
        }
      }
      cancel();
    };
    const pointerDown = () => {
      if (!dragRef.current) {
        suppressClick.current = false;
      }
    };
    const click = (event: MouseEvent) => {
      // Capture the click following pointerup before links, analytics, or
      // dismissal handlers can see it. A fresh pointerdown starts a new click.
      if (suppressClick.current || dragRef.current?.active) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const target = event.target;
      const sidebar = sidebarRef.current;
      if (
        target instanceof Node &&
        !sidebar?.contains(target) &&
        !sidebar?.closest('nav')?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        optionsRef.current.onClose(false);
      }
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        cancel();
        optionsRef.current.onClose(true);
      } else if (!dragRef.current) {
        suppressClick.current = false;
      }
    };

    document.addEventListener('pointerdown', pointerDown, true);
    document.addEventListener('pointermove', move, {
      capture: true,
      passive: false,
    });
    document.addEventListener('pointerup', drop, true);
    document.addEventListener('pointercancel', cancel, true);
    document.addEventListener('lostpointercapture', cancel, true);
    document.addEventListener('click', click, true);
    document.addEventListener('keydown', keyDown, true);
    window.addEventListener('blur', cancel);
    return () => {
      cancel();
      document.removeEventListener('pointerdown', pointerDown, true);
      document.removeEventListener('pointermove', move, true);
      document.removeEventListener('pointerup', drop, true);
      document.removeEventListener('pointercancel', cancel, true);
      document.removeEventListener('lostpointercapture', cancel, true);
      document.removeEventListener('click', click, true);
      document.removeEventListener('keydown', keyDown, true);
      window.removeEventListener('blur', cancel);
    };
  }, [options.enabled]);

  const start = (
    event: ReactPointerEvent<HTMLElement>,
    path: string,
    text: string,
  ) => {
    if (
      !options.enabled ||
      event.button !== 0 ||
      event.pointerType !== 'mouse'
    ) {
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      path,
      text,
      pointerId: event.pointerId,
      source: event.currentTarget,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      active: false,
    };
  };

  return { sidebarRef, listRef, panelRef, preview, start };
};
